// src/components/PDFProcessor.js
import React, { useState, useEffect, useRef } from 'react';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import OCRComponent from './OCRComponent';

// Funzione per convertire PDF in immagini (tutte le pagine)
const convertPDFToImages = async (pdf) => {
  try {
    console.log(`Conversione PDF in immagini... (${pdf.numPages} pagine)`);
    
    const images = [];
    
    // Processa tutte le pagine del PDF
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      console.log(`Processando pagina ${pageNum}/${pdf.numPages}...`);
      
      const page = await pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale: 2.0 }); // Scala più alta per migliore qualità
      
      // Crea un canvas per renderizzare la pagina
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      
      // Renderizza la pagina nel canvas
      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;
      
      // Converti il canvas in data URL (immagine)
      const imageDataUrl = canvas.toDataURL('image/png');
      images.push({
        pageNumber: pageNum,
        imageData: imageDataUrl
      });
    }
    
    console.log(`PDF convertito in ${images.length} immagini`);
    return images;
    
  } catch (error) {
    console.error('Errore nella conversione PDF->immagini:', error);
    throw error;
  }
};

const PDFProcessor = ({ file, onTextExtracted, onStart, onSwitchToCamera }) => {
  const [progress, setProgress] = useState(0);
  const [processingStatus, setProcessingStatus] = useState('');
  const [error, setError] = useState(null);
  const [isScannedPdf, setIsScannedPdf] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showProcessingMessage, setShowProcessingMessage] = useState(true);
  const [convertedImages, setConvertedImages] = useState([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [showOCR, setShowOCR] = useState(false);
  const [allTexts, setAllTexts] = useState([]);
  const processingCompleted = useRef(false);

  useEffect(() => {
    if (!file || processingCompleted.current || isProcessing) return;
    
    const processPDF = async () => {
      try {
        setIsProcessing(true);
        if (onStart) onStart();
        
        setProcessingStatus('Elaborazione del PDF in corso...');
        setShowProcessingMessage(true);
        console.log("Inizio elaborazione PDF:", file.name);

        // Caricamento lazy di pdfjs (non nel bundle iniziale)
        const [pdfjsLib, { default: pdfjsWorker }] = await Promise.all([
          import('pdfjs-dist'),
          import('pdfjs-dist/build/pdf.worker.entry'),
        ]);
        pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

        // Estrazione del testo direttamente nel browser usando PDF.js
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        
        let extractedText = '';
        
        // Processa ogni pagina del PDF
        for (let i = 1; i <= pdf.numPages; i++) {
          setProgress((i / pdf.numPages) * 100);
          const page = await pdf.getPage(i);
          const textContent = await page.getTextContent();
          
          // Estrai il testo dalla pagina
          const pageText = textContent.items
            .map(item => item.str)
            .join(' ');
            
          extractedText += pageText + '\n\n';
        }
        
        // Verifica se c'è abbastanza testo
        if (extractedText.length < 400) {
          console.log("PDF con testo insufficiente:", extractedText.length, "caratteri");
          setProcessingStatus('PDF scansionato rilevato. Conversione in immagini...');
          
          // Converti il PDF in immagini (tutte le pagine)
          try {
            const images = await convertPDFToImages(pdf);
            setConvertedImages(images);
            setCurrentPageIndex(0);
            setAllTexts(new Array(images.length).fill(''));
            setShowOCR(true);
            setProgress(100);
            setProcessingStatus(`Conversione completata. Avvio OCR (${images.length} pagine)...`);
          } catch (error) {
            console.error('Errore nella conversione PDF->immagini:', error);
            setIsScannedPdf(true);
            setProcessingStatus('Errore nella conversione');
            onTextExtracted("Errore nella conversione del PDF scansionato. Prova con un'immagine diretta.");
          }
        } else {
          // PDF con testo sufficiente
          setProgress(100);
          
          // Nascondi il messaggio di elaborazione dopo un breve ritardo
          setTimeout(() => {
            setShowProcessingMessage(false);
          }, 1500);
          
          // Passa il testo estratto (PDF digitale)
          onTextExtracted(extractedText, true);
        }
        
        processingCompleted.current = true;
        
      } catch (error) {
        console.error('Errore durante il processing del PDF:', error);
        setError(error.message);
        setProcessingStatus(`Errore: ${error.message}`);
        
        onTextExtracted(`Si è verificato un errore nell'elaborazione del PDF: ${error.message}`);
        processingCompleted.current = true;
      } finally {
        setIsProcessing(false);
      }
    };
    
    processPDF();
    
    return () => {
      processingCompleted.current = true;
    };
  }, [file, onTextExtracted, onStart, isProcessing]);
  
  const handleSwitchToCamera = () => {
    if (onSwitchToCamera) {
      onSwitchToCamera();
    }
  };
  
  if (!file) return null;
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 2 }}>
      {/* Mostra la progress bar e il messaggio solo durante l'elaborazione */}
      {(isProcessing || (showProcessingMessage && progress < 100)) && (
        <>
          <CircularProgress variant="determinate" value={progress} />
          <Typography variant="body2" sx={{ mt: 1 }}>
            {processingStatus || 'Elaborazione del PDF in corso...'}
          </Typography>
        </>
      )}
      
      {isScannedPdf && (
        <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold', color: 'warning.main' }}>
            Il PDF contiene troppo poco testo o è una scansione
          </Typography>
          
          <Button
            variant="contained"
            color="primary"
            startIcon={<PhotoCameraIcon />}
            onClick={handleSwitchToCamera}
            sx={{ mb: 1 }}
          >
            Scatta una foto del referto
          </Button>
          
          <Typography variant="body2" sx={{ mt: 1, textAlign: 'center', maxWidth: '80%' }}>
            Consiglio: Per i PDF scansionati, spesso è più efficace fotografare direttamente il documento
            o salvare una pagina come immagine e poi caricarla.
          </Typography>
        </Box>
      )}
      
      {error && !isScannedPdf && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="error">
            Si è verificato un errore: {error}
          </Typography>
        </Box>
      )}
      
      {/* OCR per PDF convertito in immagini (multi-pagina) */}
      {showOCR && convertedImages.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 2 }}>
          {/* Indicatore pagina corrente */}
          <Typography variant="body2" sx={{ mb: 2 }}>
            Pagina {currentPageIndex + 1} di {convertedImages.length}
          </Typography>
          
          {/* Anteprima immagine corrente */}
          <Box sx={{ mb: 2, maxWidth: '100%', maxHeight: '300px', overflow: 'hidden' }}>
            <img
              src={convertedImages[currentPageIndex].imageData}
              alt={`Pagina ${currentPageIndex + 1}`}
              style={{
                maxWidth: '100%',
                maxHeight: '300px',
                objectFit: 'contain',
                borderRadius: 8
              }}
            />
          </Box>
          
          {/* OCR per la pagina corrente */}
          <OCRComponent 
            image={convertedImages[currentPageIndex].imageData} 
            onExtracted={(text) => {
              // Salva il testo della pagina corrente
              const newTexts = [...allTexts];
              newTexts[currentPageIndex] = text;
              setAllTexts(newTexts);
              
              // Se non è l'ultima pagina, passa alla prossima
              if (currentPageIndex < convertedImages.length - 1) {
                setCurrentPageIndex(currentPageIndex + 1);
                setProcessingStatus(`OCR pagina ${currentPageIndex + 2}/${convertedImages.length}...`);
              } else {
                // Ultima pagina completata, combina tutti i testi (PDF scansionato)
                const combinedText = newTexts.join('\n\n--- PAGINA SUCCESSIVA ---\n\n');
                onTextExtracted(combinedText, false);
                setShowOCR(false);
                setProcessingStatus('OCR completato!');
              }
            }}
            onStart={() => {
              setProcessingStatus(`OCR pagina ${currentPageIndex + 1}/${convertedImages.length}...`);
            }}
          />
          
          {/* Pulsanti di navigazione (opzionale) */}
          {convertedImages.length > 1 && (
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button
                variant="outlined"
                size="small"
                disabled={currentPageIndex === 0}
                onClick={() => setCurrentPageIndex(currentPageIndex - 1)}
              >
                Pagina Precedente
              </Button>
              <Button
                variant="outlined"
                size="small"
                disabled={currentPageIndex === convertedImages.length - 1}
                onClick={() => setCurrentPageIndex(currentPageIndex + 1)}
              >
                Pagina Successiva
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default PDFProcessor;